import { Highlight } from "@CommonApi/merchflows/_merchflowId_/highlights/base-pogs";
import type { PlanogramItemUpdateStatus } from "@CommonTypes/merchflow/pog/item";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import { PlanogramLegend } from "src/components/PlanogramExtensions/PlanogramLegend";
import { isHighlightsSpecialFieldFormula } from "src/modals/Highlight/store/utils";
import {
  BULK_POUNDS,
  type PackCounts,
  countedWeightPounds,
  resolveCountedWeightPounds,
} from "@CommonUtils/pog/productWeight";
import {
  itemLoadPounds,
  shelfLimit,
  shelfLoad,
  weightOptionsForPog,
} from "src/components/Planogram/common/shelfWeight";

type HighlightableProduct = BaseProduct &
  Partial<PackCounts> & {
    updateStatus?: PlanogramItemUpdateStatus;
    getMOSPercentage?: () => number;
    getDaysOfSupply?: () => number;
    getCapacity?: () => number;
    getUnitsOnShelf?: () => number;
  };

type HighlightContext = {
  item: HighlightableProduct;
  pog: Pog;
};

type ComboValueMethodName = "getDaysOfSupply" | "getCapacity";

type ComboValueTotals = Record<ComboValueMethodName, number>;

type FormulaEvaluationContext = HighlightContext & {
  comboValuesByProductCode: Map<string, ComboValueTotals>;
  identifierValueCache: Map<string, unknown>;
};

type ComputedFieldResolver = (context: FormulaEvaluationContext) => unknown;

type Token =
  | { type: "identifier"; value: string }
  | { type: "number"; value: number }
  | { type: "string"; value: string }
  | { type: "boolean"; value: boolean }
  | { type: "operator"; value: Operator }
  | { type: "paren"; value: "(" | ")" };

type Operator = "||" | "&&" | "===" | "<=" | ">=" | "<" | ">" | "+" | "-" | "*" | "/";

type Expression =
  | { type: "literal"; value: string | number | boolean }
  | { type: "identifier"; value: string }
  | { type: "unary"; operator: "-"; operand: Expression }
  | { type: "binary"; operator: Operator; left: Expression; right: Expression };

const operatorPrecedence: Record<Operator, number> = {
  "||": 1,
  "&&": 2,
  "===": 3,
  "<": 4,
  ">": 4,
  "<=": 4,
  ">=": 4,
  "+": 5,
  "-": 5,
  "*": 6,
  "/": 6,
};

const weightOptions = (pog: Pog) => weightOptionsForPog(pog);

const computedFieldResolvers: Record<string, ComputedFieldResolver> = {
  mos: ({ item }) => item.getMOSPercentage?.() ?? 0,
  minonshelf: () => 100,
  dos: (context) => getComboValue(context.item.productCode, context, "getDaysOfSupply"),
  capacity: (context) => getComboValue(context.item.productCode, context, "getCapacity"),
  // ?? 0 only on per-item compare — never copy it into a sum.
  weight: ({ item, pog }) =>
    resolveCountedWeightPounds(item, "UNIT", weightOptions(pog))?.pounds ?? 0,
  bulk: ({ item, pog }) =>
    (countedWeightPounds(item, "UNIT", weightOptions(pog)) ?? 0) >= BULK_POUNDS,
  measured: ({ item, pog }) =>
    resolveCountedWeightPounds(item, "UNIT", weightOptions(pog))?.provenance === "measured",
  itemweight: ({ item, pog }) => {
    const { getUnitsOnShelf } = item;
    if (!getUnitsOnShelf) return 0;
    return (
      itemLoadPounds(
        {
          name: item.name,
          size: item.size,
          unitWeight: item.unitWeight,
          trayWeight: item.trayWeight,
          caseWeight: item.caseWeight,
          weightUom: item.weightUom,
          noOfUnitsInTray: item.noOfUnitsInTray,
          noOfUnitsInCase: item.noOfUnitsInCase,
          getUnitsOnShelf,
        },
        weightOptions(pog),
      ) ?? 0
    );
  },
  shelfweight: ({ item, pog }) => getShelfWeightStats(pog, item.uniqueId)?.pounds ?? 0,
  // Formulas are numeric with no way to say "unknown", so an uncapped shelf
  // resolves to 0 — the quiet direction, rather than flagging a shelf nobody
  // measured.
  shelfweightpct: ({ item, pog }) => getShelfWeightStats(pog, item.uniqueId)?.pct ?? 0,
  overloaded: ({ item, pog }) => getShelfWeightStats(pog, item.uniqueId)?.overloaded ?? false,
};

type ShelfWeightStats = {
  pounds: number;
  /** Null when the run supplied no cap for this shelf — no verdict, not 0%. */
  pct: number | null;
  overloaded: boolean;
};

type ShelfWeightIndex = {
  statsByShelfId: Map<string, ShelfWeightStats>;
  shelfIdByItemId: Map<string, string>;
};

// Weight cache keyed by pog identity so an edit cannot serve a stale board load.
const shelfWeightIndexCache = new WeakMap<Pog, ShelfWeightIndex>();

const buildShelfWeightIndex = (pog: Pog): ShelfWeightIndex => {
  const statsByShelfId = new Map<string, ShelfWeightStats>();
  const shelfIdByItemId = new Map<string, string>();

  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.getItemsPhysicallyOnBoard()) {
        shelfIdByItemId.set(item.uniqueId, shelf.uniqueId);
      }

      const load = shelfLoad(shelf);
      const limit = shelfLimit(shelf);
      // Null, not 0: "0% full" is a claim, and nobody supplied a cap here.
      const pct = limit ? (load.pounds / limit.pounds) * 100 : null;
      statsByShelfId.set(shelf.uniqueId, {
        pounds: load.pounds,
        pct,
        overloaded: pct !== null && pct > 100,
      });
    }
  }

  return { statsByShelfId, shelfIdByItemId };
};

const getShelfWeightStats = (pog: Pog, itemUniqueId: string): ShelfWeightStats | null => {
  let index = shelfWeightIndexCache.get(pog);
  if (!index) {
    index = buildShelfWeightIndex(pog);
    shelfWeightIndexCache.set(pog, index);
  }

  const shelfId = index.shelfIdByItemId.get(itemUniqueId);
  if (shelfId === undefined) {
    return null;
  }

  return index.statsByShelfId.get(shelfId) ?? null;
};

const comboValuesByProductCodeCache = new WeakMap<Pog, Map<string, ComboValueTotals>>();

const buildComboValuesByProductCode = (pog: Pog) => {
  const comboValuesByProductCode = new Map<string, ComboValueTotals>();

  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.items) {
        const existingTotals = comboValuesByProductCode.get(item.productCode) ?? {
          getDaysOfSupply: 0,
          getCapacity: 0,
        };

        existingTotals.getDaysOfSupply += item.getDaysOfSupply();
        existingTotals.getCapacity += item.getCapacity();
        comboValuesByProductCode.set(item.productCode, existingTotals);
      }
    }
  }

  return comboValuesByProductCode;
};

const getComboValuesByProductCode = (pog: Pog) => {
  const cachedComboValues = comboValuesByProductCodeCache.get(pog);
  if (cachedComboValues) {
    return cachedComboValues;
  }

  const comboValuesByProductCode = buildComboValuesByProductCode(pog);
  comboValuesByProductCodeCache.set(pog, comboValuesByProductCode);

  return comboValuesByProductCode;
};

export const getComboValue = (
  productCode: string,
  context: FormulaEvaluationContext,
  methodName: ComboValueMethodName,
) => {
  return context.comboValuesByProductCode.get(productCode)?.[methodName] ?? 0;
};

const isIdentifierStart = (char: string) => /[A-Za-z_$]/.test(char);
const isIdentifierPart = (char: string) => /[A-Za-z0-9_.$]/.test(char);

const tokenizeFormula = (formula: string): Token[] => {
  const tokens: Token[] = [];
  let index = 0;

  while (index < formula.length) {
    const char = formula[index];

    if (/\s/.test(char)) {
      index += 1;
      continue;
    }

    if (char === "(" || char === ")") {
      tokens.push({ type: "paren", value: char });
      index += 1;
      continue;
    }

    if (char === '"' || char === "'") {
      const quote = char;
      let value = "";
      index += 1;

      while (index < formula.length && formula[index] !== quote) {
        if (formula[index] === "\\" && index + 1 < formula.length) {
          value += formula[index + 1];
          index += 2;
        } else {
          value += formula[index];
          index += 1;
        }
      }

      if (formula[index] !== quote) {
        throw new Error("Unterminated string literal");
      }

      tokens.push({ type: "string", value });
      index += 1;
      continue;
    }

    const threeCharOperator = formula.slice(index, index + 3);
    if (threeCharOperator === "===") {
      tokens.push({ type: "operator", value: threeCharOperator });
      index += 3;
      continue;
    }

    const twoCharOperator = formula.slice(index, index + 2);
    if (
      twoCharOperator === "&&" ||
      twoCharOperator === "||" ||
      twoCharOperator === "<=" ||
      twoCharOperator === ">="
    ) {
      tokens.push({ type: "operator", value: twoCharOperator });
      index += 2;
      continue;
    }

    if (
      char === "<" ||
      char === ">" ||
      char === "+" ||
      char === "-" ||
      char === "*" ||
      char === "/"
    ) {
      tokens.push({ type: "operator", value: char });
      index += 1;
      continue;
    }

    if (/\d/.test(char)) {
      let rawNumber = char;
      index += 1;

      while (index < formula.length && /[\d.]/.test(formula[index])) {
        rawNumber += formula[index];
        index += 1;
      }

      const value = Number(rawNumber);
      if (Number.isNaN(value)) {
        throw new Error(`Invalid number literal ${rawNumber}`);
      }

      tokens.push({ type: "number", value });
      continue;
    }

    if (isIdentifierStart(char)) {
      let value = char;
      index += 1;

      while (index < formula.length && isIdentifierPart(formula[index])) {
        value += formula[index];
        index += 1;
      }

      if (value === "true" || value === "false") {
        tokens.push({ type: "boolean", value: value === "true" });
      } else {
        tokens.push({ type: "identifier", value });
      }
      continue;
    }

    throw new Error(`Unsupported formula character ${char}`);
  }

  return tokens;
};

const parseFormula = (formula: string): Expression => {
  const tokens = tokenizeFormula(formula);
  let index = 0;

  const parsePrimary = (): Expression => {
    const token = tokens[index];
    if (!token) {
      throw new Error("Unexpected end of formula");
    }

    index += 1;

    if (token.type === "number" || token.type === "string" || token.type === "boolean") {
      return { type: "literal", value: token.value };
    }

    if (token.type === "identifier") {
      return { type: "identifier", value: token.value };
    }

    if (token.type === "paren" && token.value === "(") {
      const expression = parseExpression();
      const closingToken = tokens[index];
      if (!closingToken || closingToken.type !== "paren" || closingToken.value !== ")") {
        throw new Error("Missing closing parenthesis");
      }

      index += 1;
      return expression;
    }

    throw new Error("Expected a value or field");
  };

  const parseUnary = (): Expression => {
    const token = tokens[index];

    if (token?.type === "operator" && token.value === "-") {
      index += 1;
      return { type: "unary", operator: "-", operand: parseUnary() };
    }

    return parsePrimary();
  };

  const parseExpression = (minimumPrecedence = 1): Expression => {
    let left = parseUnary();

    while (index < tokens.length) {
      const token = tokens[index];
      if (token.type !== "operator") {
        break;
      }

      const precedence = operatorPrecedence[token.value];
      if (precedence < minimumPrecedence) {
        break;
      }

      index += 1;
      const right = parseExpression(precedence + 1);
      left = { type: "binary", operator: token.value, left, right };
    }

    return left;
  };

  const expression = parseExpression();

  if (index < tokens.length) {
    throw new Error("Unexpected token in formula");
  }

  return expression;
};

const getNormalizedKey = (value: unknown, key: string) => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  return Object.keys(value).find((objectKey) => objectKey.toLowerCase() === key.toLowerCase());
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const getItemFieldValue = (fieldPath: string, item: HighlightableProduct) => {
  const pathParts = fieldPath.split(".");
  let value: unknown = item;

  for (const pathPart of pathParts) {
    if (!isRecord(value)) {
      return { found: false, value: undefined };
    }

    const key = getNormalizedKey(value, pathPart);
    if (!key) {
      return { found: false, value: undefined };
    }

    value = value[key];
  }

  return { found: true, value };
};

const createFormulaEvaluationContext = (
  item: HighlightableProduct,
  pog: Pog,
): FormulaEvaluationContext => {
  return {
    item,
    pog,
    comboValuesByProductCode: getComboValuesByProductCode(pog),
    identifierValueCache: new Map<string, unknown>(),
  };
};

export const getHighlightFieldValue = (fieldPath: string, item: HighlightableProduct, pog: Pog) => {
  const itemFieldValue = getItemFieldValue(fieldPath, item);

  if (itemFieldValue.found) {
    return itemFieldValue.value;
  }

  return computedFieldResolvers[fieldPath.toLowerCase()]?.(
    createFormulaEvaluationContext(item, pog),
  );
};

const getFormulaIdentifierValue = (
  fieldPath: string,
  context: FormulaEvaluationContext,
): unknown => {
  const normalizedFieldPath = fieldPath.toLowerCase();
  const cachedValue = context.identifierValueCache.get(normalizedFieldPath);

  if (context.identifierValueCache.has(normalizedFieldPath)) {
    return cachedValue;
  }

  const value =
    computedFieldResolvers[normalizedFieldPath]?.(context) ??
    getHighlightFieldValue(fieldPath, context.item, context.pog);

  context.identifierValueCache.set(normalizedFieldPath, value);

  return value;
};

const toComparableValue = (value: unknown) => {
  if (value === undefined || value === null) {
    return 0;
  }

  return value;
};

const evaluateExpression = (expression: Expression, context: FormulaEvaluationContext): unknown => {
  if (expression.type === "literal") {
    return expression.value;
  }

  if (expression.type === "identifier") {
    return getFormulaIdentifierValue(expression.value, context);
  }

  if (expression.type === "unary") {
    return -Number(toComparableValue(evaluateExpression(expression.operand, context)));
  }

  const left = evaluateExpression(expression.left, context);

  if (expression.operator === "&&") {
    return Boolean(left) && Boolean(evaluateExpression(expression.right, context));
  }

  if (expression.operator === "||") {
    return Boolean(left) || Boolean(evaluateExpression(expression.right, context));
  }

  const right = evaluateExpression(expression.right, context);

  switch (expression.operator) {
    case "===":
      return left === right;
    case "<":
      return Number(toComparableValue(left)) < Number(toComparableValue(right));
    case ">":
      return Number(toComparableValue(left)) > Number(toComparableValue(right));
    case "<=":
      return Number(toComparableValue(left)) <= Number(toComparableValue(right));
    case ">=":
      return Number(toComparableValue(left)) >= Number(toComparableValue(right));
    case "+":
      return Number(toComparableValue(left)) + Number(toComparableValue(right));
    case "-":
      return Number(toComparableValue(left)) - Number(toComparableValue(right));
    case "*":
      return Number(toComparableValue(left)) * Number(toComparableValue(right));
    case "/":
      return Number(toComparableValue(left)) / Number(toComparableValue(right));
  }

  return undefined;
};

const parsedFormulaCache = new Map<string, Expression>();

const evaluateFormula = (formula: string, item: HighlightableProduct, pog: Pog) => {
  const parsedFormula =
    parsedFormulaCache.get(formula) ??
    (() => {
      const expression = parseFormula(formula);
      parsedFormulaCache.set(formula, expression);
      return expression;
    })();

  return evaluateExpression(parsedFormula, {
    ...createFormulaEvaluationContext(item, pog),
  });
};

export const getHighlightColor = (
  highlight: Highlight,
  item: HighlightableProduct,
  pog: Pog,
  planogramLegend: PlanogramLegend,
) => {
  const trimmedFormula = highlight.formula.trim();
  const specialFieldValue = getHighlightFieldValue(trimmedFormula, item, pog);
  const specialFieldValueKey =
    specialFieldValue === undefined || specialFieldValue === null
      ? undefined
      : String(specialFieldValue);

  if (
    isHighlightsSpecialFieldFormula(trimmedFormula) &&
    specialFieldValueKey &&
    planogramLegend.formulaColorsMap[specialFieldValueKey]
  ) {
    return planogramLegend.formulaColorsMap[specialFieldValueKey];
  }

  try {
    if (evaluateFormula(trimmedFormula, item, pog) === true) {
      if (
        isHighlightsSpecialFieldFormula(trimmedFormula) &&
        specialFieldValueKey &&
        planogramLegend.formulaColorsMap[specialFieldValueKey]
      ) {
        return planogramLegend.formulaColorsMap[specialFieldValueKey];
      }

      return highlight.color;
    }
  } catch {
    console.warn(`Highlight ${highlight.name} formula ${highlight.formula} is invalid`);
  }

  return false;
};
